import { useMemo } from 'react'
import { Paper, Typography, Box, Skeleton, useTheme } from '@mui/material'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { DailyDashboardStatDto } from '@api/types/dashboard'
import { useCategoryLookup } from '@features/admin/hooks/useAdventureCategories'
import { buildCategoryColors, CHART_COLORS, FALLBACK_CATEGORY_COLOR } from './chartConfig'
import { useChartLayout } from './useChartLayout'

interface CategoryBreakdownChartProps {
  data?: DailyDashboardStatDto[]
  isLoading?: boolean
}

export function CategoryBreakdownChart({ data, isLoading }: CategoryBreakdownChartProps) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const layout = useChartLayout()
  // Names and colours come from the category rows, so a new category charts with no client change
  const { all: categoryRows, byId: categoryById } = useCategoryLookup()
  const categoryColors = useMemo(() => buildCategoryColors(categoryRows), [categoryRows])

  // Get all unique categories from the data
  const categories = useMemo(() => {
    if (!data) return []
    const categorySet = new Set<number>()
    data.forEach((day) => {
      day.completionsByCategory?.forEach((cat) => {
        categorySet.add(cat.category)
      })
    })
    return Array.from(categorySet).sort((a, b) => a - b)
  }, [data])

  const chartData = useMemo(() => {
    if (!data) return []
    return data.map((item) => {
      const dataPoint: Record<string, number | string> = {
        date: new Date(item.date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
      }

      // Initialize all categories to 0
      categories.forEach((catId) => {
        dataPoint[`category_${catId}`] = 0
      })

      // Fill in actual values (total completions = solo + group for each category)
      item.completionsByCategory?.forEach((cat) => {
        dataPoint[`category_${cat.category}`] = cat.soloCompletions + cat.groupCompletions
      })

      return dataPoint
    })
  }, [data, categories])

  const gridColor = isDark ? CHART_COLORS.gridDark : CHART_COLORS.grid
  const axisColor = isDark ? CHART_COLORS.axisDark : CHART_COLORS.axis

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
        Completions by Category
      </Typography>
      {isLoading ? (
        <Box sx={{ height: layout.height }}>
          <Skeleton variant="rectangular" height="100%" />
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={layout.height}>
          <AreaChart data={chartData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="date" tick={{ fill: axisColor, fontSize: layout.fontSize }} minTickGap={layout.minTickGap} />
            <YAxis tick={{ fill: axisColor, fontSize: layout.fontSize }} width={layout.yAxisWidth} allowDecimals={false} />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
              }}
            />
            <Legend wrapperStyle={{ fontSize: layout.fontSize + 2 }} />
            {categories.map((catId) => (
              <Area
                key={catId}
                type="monotone"
                dataKey={`category_${catId}`}
                name={categoryById.get(catId)?.displayName || `Category ${catId}`}
                stackId="1"
                stroke={categoryColors.get(catId) ?? FALLBACK_CATEGORY_COLOR}
                fill={categoryColors.get(catId) ?? FALLBACK_CATEGORY_COLOR}
                fillOpacity={0.6}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Paper>
  )
}
